import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Plus, Trash2, HelpCircle, BookOpen, RefreshCw, Zap } from 'lucide-react';
import { adminApi } from '../../lib/api';
import { toast } from 'sonner';

export const QBANK_BOARDS = ['CBSE', 'ICSE', 'SSC', 'HSC', 'IB', 'Cambridge'];
export const QBANK_GRADES_MAP = { CBSE: [5,6,7,8,9,10,11,12], ICSE: [5,6,7,8,9,10,11,12], SSC: [5,6,7,8,9,10], HSC: [11,12], IB: [5,6,7,8,9,10,11,12], Cambridge: [5,6,7,8,9,10,11,12] };

export const QuestionBankAdmin = () => {
  const [stats, setStats] = useState({ stats: [], total_questions: 0 });
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [genBoard, setGenBoard] = useState('CBSE');
  const [genGrade, setGenGrade] = useState('10');
  const [genSubject, setGenSubject] = useState('');
  const [genCount, setGenCount] = useState('30');
  const [subjects, setSubjects] = useState([]);

  useEffect(() => { fetchStats(); }, []);
  useEffect(() => { fetchSubjects(); }, [genBoard, genGrade]);

  const fetchStats = async () => {
    setLoading(true);
    try { const data = await adminApi.getQuestionBankStats(); setStats(data); }
    catch { toast.error('Failed to load stats'); }
    finally { setLoading(false); }
  };

  const fetchSubjects = async () => {
    try {
      const data = await (await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/board-exams/subjects?board=${genBoard}&grade=${genGrade}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })).json();
      setSubjects(data.subjects || []);
      setGenSubject('');
    } catch { setSubjects([]); }
  };

  const handleGenerate = async () => {
    if (!genSubject) { toast.error('Please select a subject'); return; }
    setGenerating(true);
    try {
      const result = await adminApi.generateQuestionBank(genBoard, parseInt(genGrade), genSubject, parseInt(genCount));
      toast.success(result.message);
      fetchStats();
    } catch (e) { toast.error(e.message || 'Generation failed'); }
    finally { setGenerating(false); }
  };

  const handleClear = async (board, grade, subject) => {
    if (!window.confirm(`Clear all questions for ${board} Class ${grade} ${subject}?`)) return;
    try {
      const result = await adminApi.clearQuestionBank(board, grade, subject);
      toast.success(`Cleared ${result.deleted} questions`);
      fetchStats();
    } catch { toast.error('Failed to clear'); }
  };

  return (
    <div className="space-y-6" data-testid="question-bank-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><BookOpen className="w-6 h-6" /> AI Question Bank</h1>
        <p className="text-muted-foreground text-sm mt-1">Pre-populate question banks using AI. Exams will pull from these when available.</p>
      </div>

      {/* Generator */}
      <div className="p-5 rounded-xl border bg-card space-y-4" data-testid="qbank-generator">
        <h3 className="font-semibold flex items-center gap-2"><Zap className="w-4 h-4" /> Generate Questions</h3>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Select value={genBoard} onValueChange={(v) => { setGenBoard(v); const g = QBANK_GRADES_MAP[v]; if (!g.includes(parseInt(genGrade))) setGenGrade(String(g[0])); }}>
            <SelectTrigger data-testid="qbank-board"><SelectValue /></SelectTrigger>
            <SelectContent>{QBANK_BOARDS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={genGrade} onValueChange={setGenGrade}>
            <SelectTrigger data-testid="qbank-grade"><SelectValue /></SelectTrigger>
            <SelectContent>{(QBANK_GRADES_MAP[genBoard] || []).map(g => <SelectItem key={g} value={String(g)}>Class {g}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={genSubject} onValueChange={setGenSubject}>
            <SelectTrigger data-testid="qbank-subject"><SelectValue placeholder="Subject" /></SelectTrigger>
            <SelectContent>{subjects.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={genCount} onValueChange={setGenCount}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{[10,20,30,50].map(n => <SelectItem key={n} value={String(n)}>{n} Qs</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={handleGenerate} disabled={generating || !genSubject} data-testid="qbank-generate-btn" className="w-full">
            {generating ? <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Generating...</> : <><Zap className="w-4 h-4 mr-1" /> Generate</>}
          </Button>
        </div>
      </div>

      {/* Stats Table */}
      <div className="rounded-xl border bg-card overflow-hidden">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-semibold">Question Bank ({stats.total_questions} total)</h3>
          <Button variant="outline" size="sm" onClick={fetchStats}><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
        </div>
        {loading ? <div className="skeleton h-32" /> : stats.stats.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">No questions generated yet. Use the generator above to populate the bank.</div>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-muted/80 backdrop-blur">
                <tr><th className="text-left p-3">Board</th><th className="text-left p-3">Grade</th><th className="text-left p-3">Subject</th><th className="text-center p-3">Questions</th><th className="text-center p-3">Chapters</th><th className="text-right p-3">Actions</th></tr>
              </thead>
              <tbody className="divide-y divide-border">
                {stats.stats.map((s, i) => (
                  <tr key={i} data-testid={`qbank-row-${i}`}>
                    <td className="p-3 font-medium">{s.board}</td>
                    <td className="p-3">Class {s.grade}</td>
                    <td className="p-3">{s.subject}</td>
                    <td className="p-3 text-center"><Badge>{s.count}</Badge></td>
                    <td className="p-3 text-center">{s.chapters}</td>
                    <td className="p-3 text-right">
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleClear(s.board, s.grade, s.subject)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};


// ==================== CHAPTER WEIGHTAGE ====================
